import { z } from 'zod';

/**
 * Validates the page-stream SSE envelope shape ONLY — the discriminant plus
 * the fields the client branches on. Mirrors `turnStepEventSchema`'s policy:
 * `intent` / `config` / `filters` content is model output or server-fetched
 * data validated downstream at its point of use
 * (`getValidatedPositionedItemsFromLlmContent`, the filter-apply hook), so it
 * stays `z.unknown()` — validating its internals here would false-flag
 * legitimate variety. The envelope itself is authored deterministically by
 * dash-meta's `SSEEvent` union (ai-page-generation.service.ts), so this
 * catches contract drift, not model creativity.
 *
 * Why this matters: before the envelope, a malformed `config` event was
 * swallowed by a bare catch — the page silently didn't build, with nothing
 * recorded anywhere.
 *
 * One deliberate delta from the pre-envelope cast-and-forward: the strict
 * variants are non-passthrough, so unknown TOP-LEVEL keys on a KNOWN event
 * type are stripped from the forwarded copy. Consumers read only
 * `type`/`content`, so this is unobservable today — if a producer ever adds a
 * sibling field to a known event, add it to that variant here.
 */
/**
 * Payload whose SHAPE is left to downstream validation but whose PRESENCE is
 * required: bare `z.unknown()` also accepts a MISSING `content` key, and a
 * content-less `config` event is exactly the silently-not-built page this
 * envelope exists to surface. `null` counts as absent — every consumer
 * dereferences `content` immediately (`content.items`, `content.filters`), so
 * a null would throw into the per-line swallow and reproduce the same silent
 * non-build with no telemetry.
 */
const presentUnknown = z
  .unknown()
  .refine((value): value is NonNullable<unknown> => value != null);

const pageStreamKnownEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('thinking'), content: z.string() }),
  z.object({ type: z.literal('status'), content: z.string() }),
  z.object({ type: z.literal('error'), content: z.string() }),
  z.object({ type: z.literal('verifying'), content: z.string() }),
  z.object({ type: z.literal('intent'), content: presentUnknown }),
  z.object({ type: z.literal('config'), content: presentUnknown }),
  z.object({ type: z.literal('filters'), content: presentUnknown }),
  // Produced by dash-meta but never consumed by the FE; validated only so a
  // malformed one is still visible in telemetry.
  z.object({ type: z.literal('debug_timing'), content: presentUnknown }),
  z.object({
    type: z.literal('finding'),
    // The consumed `PageDecisionDigest` fields; `action` stays a plain string
    // (not an enum) so a producer-side action addition degrades to a narrated
    // finding, not a dropped event + telemetry noise.
    content: z
      .object({ title: z.string(), action: z.string(), reason: z.string() })
      .passthrough(),
  }),
  z.object({
    type: z.literal('questions'),
    content: z
      .object({ questions: z.array(z.unknown()), triggerReason: z.string() })
      .passthrough(),
  }),
]);

/**
 * The union's discriminants, derived so the two cannot drift: a type listed
 * here without a variant would forward an event unvalidated; a variant without
 * an entry here would turn a known event into telemetry and a drop.
 */
const KNOWN_PAGE_STREAM_EVENT_TYPES: ReadonlySet<string> = new Set(
  pageStreamKnownEventSchema.options.map((option) => option.shape.type.value)
);

const envelopeShapeSchema = z.object({ type: z.string() }).passthrough();

export type PageStreamEnvelopeParseResult =
  | { ok: true; event: unknown }
  | { ok: false };

/**
 * Two-stage parse: a known event type must pass its strict variant (failure →
 * `{ ok: false }` so the consume loop can report it); an UNKNOWN event type
 * that still carries a string `type` is forwarded untouched — the pre-envelope
 * behavior, which lets the producer grow its vocabulary ahead of the FE
 * without every new event tripping telemetry.
 */
export function parsePageStreamEnvelope(
  parsed: unknown
): PageStreamEnvelopeParseResult {
  const envelope = envelopeShapeSchema.safeParse(parsed);
  if (!envelope.success) {
    return { ok: false };
  }
  if (!KNOWN_PAGE_STREAM_EVENT_TYPES.has(envelope.data.type)) {
    return { ok: true, event: envelope.data };
  }
  const known = pageStreamKnownEventSchema.safeParse(parsed);
  if (!known.success) {
    return { ok: false };
  }
  return { ok: true, event: known.data };
}
